import axios from 'axios';
import { omit, pick } from 'lodash';

import type { StoredPaymentMethod } from '@adyen/adyen-web/dist/types/types';
import type AdyenCheckoutType from '@adyen/adyen-web';

import { CreatePaymentWidgetParams, MountableElement, PaymentMethodsData,
  PaymentProviderIntegration, PaymentResult, PAYMENT_METHODS, StoredPaymentMethodData } from '../interfaces';
import { AdditionalPaymentDetails, HandleActionObject,
  PaymentWidgetState, StoredPaymentMethodCreditCard, PaymentMethodsResponse, AdyenPaymentResponse } from './interfaces';

// Lazy load AdyenCheckout only when needed
let adyenCheckoutModule: typeof AdyenCheckoutType | null = null;

const getAdyenCheckout = async (): Promise<typeof AdyenCheckoutType> => {
  if (!adyenCheckoutModule) {
    const module = await import('@adyen/adyen-web');
    adyenCheckoutModule = module.default;
  }

  return adyenCheckoutModule;
};

export class AdyenIntegration implements PaymentProviderIntegration {
  private apiHostUrl: string;
  private session: {
    cartID: string;
    paymentMethodsResponse: PaymentMethodsResponse;
    state: {
      [PAYMENT_METHODS.Card]?: PaymentWidgetState;
      [PAYMENT_METHODS.SavedCard]?: PaymentWidgetState;
      [PAYMENT_METHODS.PayPal]?: PaymentWidgetState;
    }
  } | null = null;

  constructor(configs: { apiHostUrl: string }) {
    this.apiHostUrl = configs.apiHostUrl;
  }

  //#region private methods
  private async sendPaymentDetails(
    paymentID: string,
    additionalDetails: AdditionalPaymentDetails
  ) : Promise<AdyenPaymentResponse | null> {
    try {
      const { data } = await axios.post(`${this.apiHostUrl}/paymentDetails`, {
        paymentID,
        data: additionalDetails.data
      });

      return data;
    } catch {
      return null;
    }
  }

  private isStoredCreditCard(paymentMethod: StoredPaymentMethod): paymentMethod is StoredPaymentMethodCreditCard {
    return paymentMethod.type === 'scheme';
  }

  private async createCardWidget(params: CreatePaymentWidgetParams): Promise<MountableElement> {
    const onChange = (state: PaymentWidgetState): void => {
      if (this.session) {
        this.session.state[PAYMENT_METHODS.Card] = state;
        params.onChange?.(state.isValid);
      }
    };

    const configuration = {
      locale: 'en_US',
      environment: 'test',
      clientKey: process.env.NEXT_PUBLIC_ADYEN_CLIENT_KEY,
      translations: {
        'en-US': {
          'creditCard.numberField.placeholder': 'Credit Card Number*',
          'creditCard.expiryDateField.placeholder': 'Expiring MM/YY *',
          'creditCard.cvcField.placeholder.3digits': 'CVC Code *'
        }
      },
      paymentMethodsConfiguration: {
        card: {
          enableStoreDetails: true,
          styles: {
            base: {
              fontSize: '14px'
            }
          }
        }
      },
      paymentMethodsResponse: this.session?.paymentMethodsResponse,
      onChange
    };

    const AdyenCheckout = await getAdyenCheckout();
    const checkout = await AdyenCheckout(configuration);

    return checkout.create('card');
  }

  private async createSavedCardWidget(params: CreatePaymentWidgetParams): Promise<MountableElement> {
    const onChange = (state: PaymentWidgetState): void => {
      if (this.session) {
        this.session.state[PAYMENT_METHODS.SavedCard] = state;
        params.onChange?.(state.isValid);
      }
    };

    const configuration = {
      locale: 'en_US',
      environment: 'test',
      hideCVC: true,
      clientKey: process.env.NEXT_PUBLIC_ADYEN_CLIENT_KEY,
      paymentMethodsConfiguration: {
        storedCard: {
          styles: {
            base: {
              marginTop: '15px',
              fontSize: '14px'
            }
          }
        }
      },
      paymentMethodsResponse: this.session?.paymentMethodsResponse,
      onChange
    };

    const AdyenCheckout = await getAdyenCheckout();
    const checkout = await AdyenCheckout(configuration);

    const [ storedPaymentMethod ] = checkout.paymentMethodsResponse.storedPaymentMethods;

    return checkout.create('card', storedPaymentMethod);
  }

  private async createPaypalWidget(params: CreatePaymentWidgetParams): Promise<MountableElement> {
    const configuration = {
      locale: 'en_US',
      environment: 'test',
      amount: params.amount,
      clientKey: process.env.NEXT_PUBLIC_ADYEN_CLIENT_KEY,
      paymentMethodsResponse: this.session?.paymentMethodsResponse,
      onSubmit: async (
        state: PaymentWidgetState,
        action: HandleActionObject
      ): Promise<void> => {
        if (this.session) {
          if (state.isValid && state.data.paymentMethod.type === 'paypal') {
            this.session.state[PAYMENT_METHODS.PayPal] = state;
            const response = await this.makePaymentRequestInner(PAYMENT_METHODS.PayPal, !!params.isSubscription);
            if (response?.action) {
              action.handleAction(response.action);
            }
            params.onSubmit?.(response.resultCode, response.refusalReason);
          }
        }
      },
      onAdditionalDetails: async (
        state: AdditionalPaymentDetails,
        component: HandleActionObject
      ): Promise<void> => {
        const result = await this.sendPaymentDetails?.(this.session?.paymentMethodsResponse.payment.id ?? '', state);
        if (result) {
          if (result.action) {
            component.handleAction(result.action);
          } else {
            params.onSubmit?.(result.resultCode);
          }
        }
      }
    };

    const AdyenCheckout = await getAdyenCheckout();
    const checkout = await AdyenCheckout(configuration as unknown);

    return checkout.create('paypal');
  }

  private async makePaymentRequestInner(paymentMethodType: PAYMENT_METHODS, isSubscription: boolean)
  : Promise<AdyenPaymentResponse | null> {
    const paymentMethodState = this.session?.state[paymentMethodType];
    if (paymentMethodState) {

      const { data } = await axios.post(`${this.apiHostUrl}/makePaymentRequest`, {
        cartID: this.session?.cartID,
        paymentID: this.session?.paymentMethodsResponse.payment.id,
        paymentMethod: paymentMethodState.data.paymentMethod,
        storePaymentMethod: paymentMethodState.data.storePaymentMethod,
        subscription: isSubscription
      });

      return data;
    }

    return null;
  }

  //#endregion

  public async initiateSession(cartID: string, version: number): Promise<PaymentMethodsData> {
    const { data } = await axios.post<PaymentMethodsResponse>(`${this.apiHostUrl}/getPaymentMethods`, {
      cartID,
      version
    });

    this.session = {
      cartID,
      paymentMethodsResponse: data,
      state: {}
    };

    return {
      paymentId: data.payment.id,
      availablePaymentMethods: data.paymentMethods.map(
        paymentMethod => paymentMethod.type
      ),
      storedPaymentMethods: data.storedPaymentMethods?.filter(this.isStoredCreditCard)?.map(
        paymentMethod => pick<StoredPaymentMethodCreditCard, keyof StoredPaymentMethodData>(
          paymentMethod, [ 'name', 'expiryYear', 'expiryMonth', 'lastFour' ]
        )
      )
    };
  }

  public async createPaymentWidget(params: CreatePaymentWidgetParams): Promise<MountableElement> {
    let uiElement: MountableElement;
    switch (params.paymentMethodType) {
    case PAYMENT_METHODS.Card:
      uiElement = await this.createCardWidget(params);
      break;
    case PAYMENT_METHODS.SavedCard:
      uiElement = await this.createSavedCardWidget(params);
      break;
    case PAYMENT_METHODS.PayPal:
      uiElement = await this.createPaypalWidget(params);
      break;
    default:
      throw new Error();
    }

    return uiElement;
  }

  public async makePaymentRequest(
    paymentId: string,
    paymentMethodType: PAYMENT_METHODS,
    isSubscription: boolean
  ): Promise<PaymentResult | null> {

    if (paymentId === this.session?.paymentMethodsResponse.payment.id) {
      const result = await this.makePaymentRequestInner(paymentMethodType, isSubscription);

      if (result) return omit(result, 'action');
    }

    return null;
  }

  public async cancelPayment<Order>(orderId: string, lineItemId: string): Promise<Order | null> {
    const { data } = await axios.post(`${this.apiHostUrl}/cancelSubscription`, {
      orderId,
      lineItemId
    });

    if (data.message !== 'canceled') {
      throw new Error(data.message);
    }

    return data.order;
  }

  public endSession (): void {
    this.session = null;
  }
}
